import {useEffect, useState, useMemo, useRef, useCallback} from 'react'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useVerifyHash} from '../../hooks'
import {isValidGoriSignatureHash} from '../../utils/validation'
import {clearLabelSessionStorage} from '../../utils/sessionStorageUtils'
import {useFraudDetection} from '../../hooks/useFraudDetection'
import {
  validateStorageData,
  isValidDataUrl,
  isValidOcrData,
  normalizeStatus,
  STATUS_CONFIG,
} from '../../utils/verifyUtils'
import {LabelPreview} from './LabelPreview'
import {LoadingState} from './LoadingState'
import {StatusAlert} from './StatusAlert'

type ManifestStatus = 'paid' | 'pending' | 'expired' | 'fraud' | 'invalid'

export function GoriSignatureVerify() {
  const {hash} = useParams<{hash: string}>()
  const intl = useIntl()

  useEffect(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) {
      setErrorStatus('invalid')
    }
  }, [hash])

  useEffect(() => {
    // Cleanup on mount to clear any stale data
    if (hash && isValidGoriSignatureHash(hash)) {
      clearLabelSessionStorage(hash)
    }

    return () => {
      if (hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }
  }, [hash])

  // Also add cleanup on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [hash])

  const {data, error, isLoading} = useVerifyHash(hash)
  const [errorStatus, setErrorStatus] = useState<'expired' | 'invalid' | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)

  const uploadedImage = useMemo(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) return null
    const stored = sessionStorage.getItem(`label_image_${hash}`)
    const validated = validateStorageData(stored)
    return validated && isValidDataUrl(validated) ? validated : null
  }, [hash])

  const ocrAddresses = useMemo(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) return null
    const stored = sessionStorage.getItem(`label_ocr_${hash}`)
    if (!stored) return null

    try {
      const parsed = JSON.parse(stored)
      return isValidOcrData(parsed) ? parsed : null
    } catch {
      return null
    }
  }, [hash])

  const ocrRegions = useMemo(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) return null
    const stored = sessionStorage.getItem(`label_ocr_${hash}`)
    if (!stored) return null

    try {
      const parsed = JSON.parse(stored)
      return parsed?.regions || null
    } catch {
      return null
    }
  }, [hash])

  const [expectedLabelLoaded, setExpectedLabelLoaded] = useState(false)

  useEffect(() => {
    const img = new Image()
    img.src = '/media/gori/gori-signature/label.png'
    img.onload = () => setExpectedLabelLoaded(true)
    img.onerror = () => setExpectedLabelLoaded(true)
  }, [])

  useEffect(() => {
    const cleanupData = () => {
      if (hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }

    return cleanupData
  }, [hash])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hash])

  const isManualEntry = !uploadedImage

  const {hasAddressMismatch, hasLabelBarcodeMismatch, fieldStatus} = useFraudDetection(
    data,
    ocrAddresses,
    isManualEntry
  )

  const status: ManifestStatus =
    errorStatus ||
    (hasAddressMismatch || hasLabelBarcodeMismatch
      ? 'fraud'
      : data?.valid
      ? normalizeStatus(data.manifest_status)
      : 'invalid')
  const statusConfig = STATUS_CONFIG[status]
  const showSingleLabel = isManualEntry || status === 'expired' || status === 'invalid'

  useEffect(() => {
    if (isLoading) return

    if (error) {
      setErrorStatus('invalid')
    } else if (!data) {
      setErrorStatus('invalid')
    } else if (data.valid === false) {
      const errorMessage = data.error || ''
      if (errorMessage.includes('expired') || errorMessage.includes('no longer be used')) {
        setErrorStatus('expired')
      } else {
        setErrorStatus('invalid')
      }
    } else if (!data.tracking_number) {
      setErrorStatus('invalid')
    }
  }, [data, error, isLoading])

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const handleScroll = () => {
      const scrollLeft = scrollElement.scrollLeft
      const width = scrollElement.offsetWidth
      const index = Math.round(scrollLeft / width)
      setActiveSlide(index)
    }

    scrollElement.addEventListener('scroll', handleScroll, {passive: true})
    return () => scrollElement.removeEventListener('scroll', handleScroll)
  }, [])

  const addresses = useMemo(() => {
    if (!data || !data.valid) return {from: 'N/A', to: 'N/A', tracking: 'N/A'}

    const from = [
      [data.from_street1, data.from_street2].filter(Boolean).join(' '),
      [data.from_city, data.from_state, data.from_zip].filter(Boolean).join(' '),
    ].join('\n')

    const to = [
      [data.to_street1, data.to_street2].filter(Boolean).join(' '),
      [data.to_city, data.to_state, data.to_zip].filter(Boolean).join(' '),
    ].join('\n')

    const tracking =
      data.tracking_number?.match(/.{1,4}/g)?.join(' ') || data.tracking_number || 'N/A'

    return {fromZip: data.from_zip, from, to, tracking}
  }, [data])

  const handleIndicatorClick = useCallback((index: number) => {
    setActiveSlide(index)
    if (scrollRef.current) {
      const width = scrollRef.current.offsetWidth
      scrollRef.current.scrollTo({
        left: width * index,
        behavior: 'smooth',
      })
    }
  }, [])

  if (isLoading || !expectedLabelLoaded) {
    return <LoadingState />
  }

  return (
    <div
      className='gori-signature-results d-flex flex-column align-items-center justify-content-center p-4'
      role='main'
      aria-live='polite'
    >
      <div className='gori-signature-header-wrapper text-center mb-5'>
        <img
          src='/media/logos/gori_signature_logo.png'
          alt={intl.formatMessage({id: 'GORI_SIGNATURE_TITLE'})}
          className='gori-signature-logo mb-5'
        />
        {showSingleLabel && status !== 'invalid' ? (
          <p className='fs-4 text-dark'>
            {intl.formatMessage(
              {id: 'GORI_SIGNATURE_RESULTS_MESSAGE'},
              {
                newLine: <br />,
              }
            )}
          </p>
        ) : (
          <p className='fs-4 text-dark'>
            {intl.formatMessage({id: 'GORI_SIGNATURE_RESULTS_MESSAGE_AUTHENTICATED'})}
          </p>
        )}
      </div>

      <div className='gori-signature-content-wrapper'>
        {status !== 'invalid' && !(isManualEntry && status === 'expired') && (
          <div className='gori-signature-comparison-card mb-5'>
            <div className='row g-4' ref={scrollRef}>
              {!showSingleLabel && (
                <div className='col-12 col-lg-6'>
                  <LabelPreview
                    image={uploadedImage || ''}
                    alt={intl.formatMessage({id: 'GORI_SIGNATURE_YOUR_LABEL'})}
                    showAddresses={false}
                    status={status}
                    addresses={addresses}
                    fieldStatus={fieldStatus}
                    regions={ocrRegions}
                  />
                </div>
              )}
              {showSingleLabel ? (
                status === 'expired' ? (
                  <div className='col-12'>
                    <LabelPreview
                      image={uploadedImage || ''}
                      alt={intl.formatMessage({id: 'GORI_SIGNATURE_YOUR_LABEL'})}
                      showAddresses={false}
                      status={status}
                      addresses={addresses}
                      fieldStatus={fieldStatus}
                      regions={ocrRegions}
                    />
                  </div>
                ) : (
                  <div className='col-12'>
                    <LabelPreview
                      image='/media/gori/gori-signature/label.png'
                      alt={intl.formatMessage({id: 'GORI_SIGNATURE_EXPECTED_LABEL'})}
                      showAddresses={true}
                      status={status}
                      addresses={addresses}
                      fieldStatus={fieldStatus}
                    />
                  </div>
                )
              ) : (
                <div className='col-12 col-lg-6'>
                  <LabelPreview
                    image='/media/gori/gori-signature/label.png'
                    alt={intl.formatMessage({id: 'GORI_SIGNATURE_EXPECTED_LABEL'})}
                    showAddresses={true}
                    status={status}
                    addresses={addresses}
                    fieldStatus={fieldStatus}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {!showSingleLabel && (
          <div className='gori-signature-scroll-indicators mb-5'>
            <div
              className={activeSlide === 0 ? 'indicator active' : 'indicator'}
              onClick={() => handleIndicatorClick(0)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleIndicatorClick(0)
                }
              }}
              aria-label='View your label'
            ></div>
            <div
              className={activeSlide === 1 ? 'indicator active' : 'indicator'}
              onClick={() => handleIndicatorClick(1)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleIndicatorClick(1)
                }
              }}
              aria-label='View expected label'
            ></div>
          </div>
        )}

        <StatusAlert status={status} statusConfig={statusConfig} />
      </div>
    </div>
  )
}
